import React, { useReducer, useEffect, useCallback } from 'react';
import { appReducer } from './appReducer';
import { AppActionTypes } from './actions';
import { initialState, AppContext } from './appContext';
import { IAppError, ILoggedUser } from 'types';
import { PRE_SELECTED_CUSTOMER_ID_FOR_MENU } from 'appConstants';
import { serviceGetToken, serviceSendEmail, serviceSendNewPassword } from 'services/api/authorization';
import { serviceGetUserInfo } from 'services/api/users';
import { serviceGetFeatureFlags } from 'services/api/additionalInformation';
import jwt_decode from 'jwt-decode';
import { createAppError, createAppErrorMessage } from 'utils/helpers';
import { HttpTransportType, HubConnection, HubConnectionBuilder } from '@microsoft/signalr';

const AppState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(appReducer, initialState);

	// ----- Additional features ----- //
	const addAppError = (error: IAppError) => {
		dispatch({ type: AppActionTypes.ADD_APP_ERROR, payload: { error } });
	};

	const removeAppError = (errorID: string) => {
		const updatedAppErrors = state.appErrors.filter((el) => el.id !== errorID);
		dispatch({ type: AppActionTypes.REMOVE_APP_ERROR, payload: { appErrors: updatedAppErrors } });
	};

	const showLoader = useCallback(() => dispatch({ type: AppActionTypes.SHOW_LOADER }), []);

	const hideLoader = useCallback(() => dispatch({ type: AppActionTypes.HIDE_LOADER }), []);

	const setUploadProgress = (progress: number) => {
		dispatch({ type: AppActionTypes.SET_UPLOAD_PROGRESS, payload: { uploadProgress: progress } });
	};

	const setEntityNameForHeader = (name: string) => {
		dispatch({ type: AppActionTypes.SET_ENTITY_NAME_FOR_HEADER, payload: { entityNameForHeader: name } });
	};
	// ----- Additional features ----- //

	// ----- Feature flags ----- //
	const featureFlagsCheck = async () => {
		try {
			const response = await serviceGetFeatureFlags();
			dispatch({
				type: AppActionTypes.SET_FEATURE_FLAGS,
				payload: {
					featureFlags: {
						...response.data,
						HAS_FISCAL_RECEIPT_DIALOG_BACKGROUND: !!process.env.HAS_FISCAL_RECEIPT_DIALOG_BACKGROUND,
						HAS_ADYEN_ACQUIRER: !!process.env.HAS_ADYEN_ACQUIRER,
						HAS_PORTION_IN_MENU_V2: !!process.env.HAS_PORTION_IN_MENU_V2,
						HAS_NFC_CARDS: !!process.env.HAS_NFC_CARDS,
						HAS_CHARITY: !!process.env.HAS_CHARITY,
						HAS_KM_VAT_ID: !!process.env.HAS_KM_VAT_ID,
					},
				},
			});
		} catch (err) {
			const error = err as Error;
			const errorMessage = createAppErrorMessage(err, 'featureFlagsNotPulled');
			const appError = createAppError(error);
			addAppError({ ...appError, message: errorMessage });
		}
	};
	// ----- Feature flags ----- //

	// ----- Authorization and everything connected with it ----- //
	const logIn = async (login: string, password: string) => {
		try {
			const response = await serviceGetToken(login, password);
			const dataFromJwtToken = jwt_decode<ILoggedUser>(response.data.accessToken);
			localStorage.setItem('accessToken', response.data.accessToken);
			localStorage.setItem('refreshToken', response.data.refreshToken);
			const userInfoResponse = await serviceGetUserInfo(dataFromJwtToken.userId);
			dispatch({
				type: AppActionTypes.LOG_IN,
				payload: {
					loggedUser: {
						userId: dataFromJwtToken.userId,
						user: dataFromJwtToken.user,
						role: dataFromJwtToken.role,
						filePath: userInfoResponse.data.user.filePath,
						customers: userInfoResponse.data.user.customers,
						restaurants: userInfoResponse.data.user.restaurants,
					},
				},
			});
			featureFlagsCheck();
			return 'success';
		} catch (err) {
			// const error = err as Error;
			// const appError = createAppError(error);
			// addAppError(appError);
			return 'fail';
		}
	};

	const passwordReset = async (email: string) => {
		try {
			await serviceSendEmail(email);
			return 'emailExists';
		} catch (err) {
			// const error = err as Error;
			// const appError = createAppError(error);
			// addAppError(appError);
			return 'fail';
		}
	};

	const createNewPassword = async (password: string, urlParams: { passwordResetToken: string; email: string }) => {
		try {
			await serviceSendNewPassword(password, urlParams);
			return 'newPassIsCreated';
		} catch (err) {
			// const error = err as Error;
			// const appError = createAppError(error);
			// addAppError(appError);
			return 'fail';
		}
	};

	const setUserData = async () => {
		const accessToken = localStorage.getItem('accessToken');
		if (accessToken) {
			try {
				const dataFromJwtToken = jwt_decode<ILoggedUser>(accessToken);
				const userInfoResponse = await serviceGetUserInfo(dataFromJwtToken.userId);
				dispatch({
					type: AppActionTypes.LOG_IN,
					payload: {
						loggedUser: {
							userId: dataFromJwtToken.userId,
							user: dataFromJwtToken.user,
							role: dataFromJwtToken.role,
							filePath: userInfoResponse.data.user.filePath,
							customers: userInfoResponse.data.user.customers,
							restaurants: userInfoResponse.data.user.restaurants,
						},
					},
				});
				featureFlagsCheck();
			} catch (err) {
				const error = err as Error;
				const errorMessage = createAppErrorMessage(error, 'dataNotPulled');
				throw new Error(errorMessage);
			}
		} else {
			localStorage.removeItem('accessToken');
			localStorage.removeItem('refreshToken');
			localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);
			window.location.href = '/';
		}
	};

	useEffect(() => {
		const refreshToken = localStorage.getItem('refreshToken');
		const authorizationPaths = ['/', '/password-reset', '/create-new-password', '/healthcheck'];
		if (!refreshToken && !authorizationPaths.includes(document.location.pathname)) {
			window.location.href = '/';
		} else if (refreshToken && authorizationPaths.includes(document.location.pathname)) {
			window.location.href = '/home';
		}
		if (refreshToken) {
			setUserData();
		}
	}, []);
	// ----- Authorization and everything connected with it ----- //

	// ----- Notifications ----- //
	useEffect(() => {
		if (state.loggedUser.userId) {
			const newConnection = new HubConnectionBuilder()
				.withUrl(`${process.env.WITHOUT_SECURITY_GATEWAY_URL}/hub/notifications`, {
					withCredentials: false,
					skipNegotiation: true,
					transport: HttpTransportType.WebSockets,
				})
				.withAutomaticReconnect()
				.build();
			dispatch({
				type: AppActionTypes.SET_NOTIFICATIONS_CONNECTION,
				payload: {
					notificationsConnection: newConnection,
				},
			});
			return () => {
				newConnection.stop();
			};
		}
	}, [state.loggedUser.userId]);
	// ----- Notifications ----- //

	// ----- Pathnames history ----- //
	useEffect(() => {
		dispatch({
			type: AppActionTypes.SET_PATHNAMES_HISTORY,
			payload: {
				pathnamesHistory: [...state.pathnamesHistory, window.location.pathname],
			},
		});
	}, [window.location.pathname]);
	// ----- Pathnames history ----- //

	return (
		<AppContext.Provider
			value={{
				addAppError,
				removeAppError,
				showLoader,
				hideLoader,
				setUploadProgress,
				setEntityNameForHeader,
				logIn,
				passwordReset,
				createNewPassword,
				setUserData,
				appErrors: state.appErrors,
				loggedUser: state.loggedUser,
				isLoading: state.isLoading,
				uploadProgress: state.uploadProgress,
				notificationsConnection: state.notificationsConnection,
				entityNameForHeader: state.entityNameForHeader,
				pathnamesHistory: state.pathnamesHistory,
				featureFlags: state.featureFlags,
			}}>
			{children}
		</AppContext.Provider>
	);
};

export default AppState;
